import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { ReportHeader, SummaryCards, FilterPanel, ExportButtons } from '../../components/reports';
import { D3Timeline } from '../../components/charts';
import { getDebtorFilings } from '../../services/project750kService';
import { exportToExcel, exportToPDF } from '../../utils/exportUtils';

const PageContainer = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const SearchContainer = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  font-size: 16px;
  border: 2px solid #0077B5;
  border-radius: 4px;
  font-family: 'Segoe UI', sans-serif;

  &:focus {
    outline: none;
    border-color: #005885;
  }
`;

const SearchButton = styled.button`
  background-color: #0077B5;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 10px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #005885;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ChartSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const TableContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
    font-family: 'Segoe UI', sans-serif;
  }

  th {
    background-color: #0077B5;
    color: white;
    font-weight: 600;
    position: sticky;
    top: 0;
  }

  tr:hover {
    background-color: #f8f9fa;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  font-size: 18px;
  color: #0077B5;
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const InfoBanner = styled.div`
  background-color: #e3f2fd;
  color: #1565c0;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-family: 'Segoe UI', sans-serif;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;

  strong {
    font-weight: 600;
  }
`;

const DebtorLookup = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dataset, setDataset] = useState('all');
  const [filings, setFilings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setError('Please enter a debtor name to search');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const result = await getDebtorFilings(searchTerm, dataset);
      setFilings(result.filings || []);
      setSummary(result.summary || {});
      setMeta(result.meta || null);
    } catch (err) {
      setError(`Error loading debtor filings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = useCallback(async () => {
    try {
      await exportToExcel(filings, `Debtor_Filings_${searchTerm}`, 'Filings', { debtor: searchTerm, dataset });
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    }
  }, [filings, searchTerm, dataset]);

  const handleExportPDF = useCallback(async () => {
    try {
      await exportToPDF({
        title: `Debtor Filings Report: ${searchTerm}`,
        data: filings.slice(0, 100),
        chartElementId: 'timeline-chart',
        summary: {
          'Total Filings': summary?.total || 0,
          'LB4 Filings': summary?.lb4Count || 0,
          'ListC Filings': summary?.listcCount || 0,
          'Debtor Name': searchTerm
        },
        filename: `Debtor_Filings_${searchTerm}`,
        filters: { debtor: searchTerm, dataset }
      });
    } catch (err) {
      setError(`PDF export failed: ${err.message}`);
    }
  }, [filings, searchTerm, dataset, summary]);

  const filters = [
    {
      type: 'select',
      label: 'Dataset',
      value: dataset,
      onChange: setDataset,
      options: [
        { value: 'all', label: 'All Datasets' },
        { value: 'lb4', label: 'LB4 Only' },
        { value: 'listc', label: 'ListC Only' }
      ]
    }
  ];

  const summaryCards = summary ? [
    { label: 'Total Filings', value: summary.total },
    { label: 'LB4 Filings', value: summary.lb4Count },
    { label: 'ListC Filings', value: summary.listcCount }
  ] : [];

  // Extract date from record ID
  // Format: "2007F054574 - 05/29/2007|Hay|33578" or "2007F054574"
  const parseDateFromRecordId = (recordIdStr) => {
    if (!recordIdStr) return null;
    
    const str = recordIdStr.toString();
    
    // Try to extract date from pattern like " - 05/29/2007"
    const dateMatch = str.match(/\s+-\s+(\d{2})\/(\d{2})\/(\d{4})/);
    if (dateMatch) {
      const month = parseInt(dateMatch[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(dateMatch[2], 10);
      const year = parseInt(dateMatch[3], 10);
      return new Date(year, month, day);
    }
    
    // Fallback: extract year from beginning of record ID
    // Formats: "2007F054574" (year + F + number) or "20192084809" (year + number)
    const yearMatch = str.match(/^(\d{4})/);
    if (yearMatch) {
      const year = parseInt(yearMatch[1], 10);
      // Use Jan 1 of that year if we can't extract a specific date
      if (year >= 1900 && year <= 2030) {
        return new Date(year, 0, 1); // Jan 1 of that year
      }
    }
    
    return null;
  };

  // Prepare timeline data with real dates from record IDs
  const timelineData = filings
    .slice(0, 50)
    .map((filing) => {
      const recordId = filing.recordId || filing.UCCRecordID || '';
      const parsedDate = parseDateFromRecordId(recordId);
      
      // Use parsed date if available, otherwise fall back to a default date (sorted to end)
      const date = parsedDate || new Date(1900, 0, 1);
      
      return {
        date,
        label: filing.debtorName || filing.DebtorName || 'Unknown',
        value: 1,
        hasDate: !!parsedDate
      };
    })
    // Sort by date (oldest first) so timeline flows correctly
    .sort((a, b) => a.date - b.date);

  // Calculate dynamic height based on maximum labels at a single date
  // Group labels by date (within 1 day tolerance for same date grouping)
  const dateGroups = new Map();
  timelineData.forEach((item, index) => {
    // Round date to nearest day for grouping
    const dateKey = new Date(item.date.getFullYear(), item.date.getMonth(), item.date.getDate()).getTime();
    
    if (!dateGroups.has(dateKey)) {
      dateGroups.set(dateKey, { above: [], below: [] });
    }
    
    // Alternate above/below based on index (same as in D3Timeline component)
    const group = dateGroups.get(dateKey);
    if (index % 2 === 0) {
      group.above.push(item);
    } else {
      group.below.push(item);
    }
  });

  // Find maximum labels on a single date
  let maxLabelsAbove = 0;
  let maxLabelsBelow = 0;
  dateGroups.forEach((group) => {
    maxLabelsAbove = Math.max(maxLabelsAbove, group.above.length);
    maxLabelsBelow = Math.max(maxLabelsBelow, group.below.length);
  });

  // Calculate dynamic height more conservatively:
  // - Margin top: 80px (for labels above)
  // - Space for labels above: maxLabelsAbove * 18px + 40px base offset
  // - Timeline center space: 50px
  // - Space for labels below: maxLabelsBelow * 18px + 40px base offset
  // - Margin bottom: 80px (for axis labels)
  const labelSpacing = 18;
  const baseOffset = 40; // Base offset from timeline
  const timelineCenterSpace = 50; // Space for the timeline line itself
  const margins = 160; // top 80 + bottom 80
  
  const dynamicHeight = margins + timelineCenterSpace + 
    (maxLabelsAbove * labelSpacing) + baseOffset + 
    (maxLabelsBelow * labelSpacing) + baseOffset;

  const timelineHeight = `${Math.max(300, Math.min(dynamicHeight, 1500))}px`; // Min 300px, max 1500px

  return (
    <PageContainer>
      <ReportHeader icon="🔍" title="Search Debtor Filings" />

      <SearchContainer>
        <SearchInput
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Enter debtor name to search..."
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <SearchButton onClick={handleSearch} disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </SearchButton>
      </SearchContainer>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {hasSearched && !loading && (
        <>
          {meta && (
            <InfoBanner>
              ℹ️ <strong>Search Results:</strong> Scanned {meta.recordsScanned?.toLocaleString() || 0} records
              {meta.hasMore && ' (partial results - more data available)'}
            </InfoBanner>
          )}

          <FilterPanel filters={filters} />
          
          {summary && <SummaryCards cards={summaryCards} />}
          
          <ExportButtons
            onExportExcel={handleExportExcel}
            onExportPDF={handleExportPDF}
            loading={loading}
            disableExcel={filings.length === 0}
            disablePDF={filings.length === 0}
          />

          {filings.length > 0 && (
            <>
              <ChartSection id="timeline-chart">
                <h3 style={{ fontFamily: 'Segoe UI', marginBottom: '20px' }}>Filing Timeline (First 50 Records)</h3>
                <D3Timeline
                  data={timelineData}
                  dateKey="date"
                  labelKey="label"
                  height={timelineHeight}
                />
              </ChartSection>

              <TableContainer>
                <h3 style={{ fontFamily: 'Segoe UI', marginBottom: '20px' }}>
                  All Filings ({filings.length} records)
                </h3>
                <Table>
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Debtor Name</th>
                      <th>Debtor ID</th>
                      <th>Record ID</th>
                      <th>Counties</th>
                      <th>Farm Product</th>
                      <th>Secured Parties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filings.map((filing, index) => {
                      // Extract just the record ID from the full string
                      // Format: "2007F054574 - 05/29/2007|Hay|33578" -> "2007F054574"
                      const extractRecordId = (recordIdStr) => {
                        if (!recordIdStr) return 'N/A';
                        // Extract the ID part before the first space or dash
                        const match = recordIdStr.toString().match(/^([^\s-]+)/);
                        return match ? match[1] : recordIdStr;
                      };

                      const recordId = filing.recordId || filing.UCCRecordID || 'N/A';
                      const cleanRecordId = recordId !== 'N/A' ? extractRecordId(recordId) : 'N/A';

                      return (
                        <tr key={index}>
                          <td>{filing.source}</td>
                          <td>{filing.debtorName || filing.DebtorName || 'N/A'}</td>
                          <td>{filing.debtorId || filing.DebtorID || 'N/A'}</td>
                          <td>{cleanRecordId}</td>
                          <td>{filing.counties || filing.DebtorAddress?.split(',')[1] || 'N/A'}</td>
                          <td>{filing.farmProduct || filing.FarmProducts || 'N/A'}</td>
                          <td>{filing.securedParties || filing.SecuredParty || 'N/A'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableContainer>
            </>
          )}

          {filings.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No filings found for "{searchTerm}"
            </div>
          )}
        </>
      )}

      {loading && <LoadingSpinner>Loading debtor filings...</LoadingSpinner>}
    </PageContainer>
  );
};

export default DebtorLookup;

